// PanelsGroup.js
import { Badge } from "antd";
import { AreaChartOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Badge as ReactStrapBadge, Col, Row, Card, CardHeader, CardBody, Button } from "reactstrap";
import React, { useState, useMemo, useEffect } from "react";
import Pannel from "./Pannel";
import ModalLine from "./LineModal";
import { getTodayRange, toJalaliString } from "../../Utils/dateUtils";

const PanelsGroup = ({
  index,
  PO_group,
  lang_choiced,
  plantId,
  Last_Productions,
  serial_number,
  panelOrderList,
  setPanelOrderList,
  itemsPerRow = 10,
  editMode,
  setEditMode,
}) => {
  // استفاده از رشته‌های شمسی به عنوان state (فرمت: 'jYYYY-jMM-jDD')
  const [dateHOC, setDateHOC] = useState(() => getTodayRange());
  const [showModalLine, setShowModalLine] = useState(false);

  // لاگ برای دیباگ
  useEffect(() => {
    console.log(`PanelsGroup ${index} - dateHOC:`, dateHOC);
  }, [dateHOC, index]);

  // مرتب‌سازی بهینه‌سازها بر اساس order_index
  const sortedOptimizers = useMemo(() => {
    if (!PO_group?.optimizers) return [];

    const baseOrder = PO_group.order_index || "1-1";
    const [p1, p2] = baseOrder.split("-");

    // جدا کردن بهینه‌سازهای با order_index مشخص و نامشخص
    const defined = PO_group.optimizers
      .filter((o) => o.order_index && o.order_index !== "0-0-0")
      .sort((a, b) => {
        const [, , a3] = a.order_index.split("-");
        const [, , b3] = b.order_index.split("-");
        return parseInt(a3 || 0) - parseInt(b3 || 0);
      });

    const undefinedOpts = PO_group.optimizers.filter((o) =>
      !o.order_index || o.order_index === "0-0-0"
    );

    // ترکیب و اختصاص order_index به موارد نامشخص
    const completed = [
      ...defined,
      ...undefinedOpts.map((opt, i) => ({
        ...opt,
        order_index: `${p1}-${p2}-${defined.length + i + 1}`,
      })),
    ];

    return completed;
  }, [PO_group]);

  const [optimizersState, setOptimizersState] = useState(sortedOptimizers);

  // همگام‌سازی زمانی که sortedOptimizers تغییر کرد
  useEffect(() => {
    setOptimizersState(sortedOptimizers);
  }, [sortedOptimizers]);

  // بررسی آخرین تولید گیت‌وی
  const checkProductionTime = (prod) => {
    if (!prod) return null;

    const static_date = new Date();
    static_date.setMinutes(static_date.getMinutes() - 10);
    const int_static_date = static_date.getTime();

    if (int_static_date < parseInt(prod?.date_time) && prod?.date_time) {
      return prod;
    }
    return null;
  };

  // تبدیل واحد توان
  const setPowerUnit = (power) => (power >= 1000 ? "kW" : "W");
  const setPowerNumber = (power) => (power >= 1 ? power : power * 1000);

  const group_data = checkProductionTime(PO_group.gateway?.group_data?.[0]);

  // تشخیص وضعیت آنلاین/آفلاین
  const BadgeTextDetector = (prod) =>
    prod ? lang_choiced.pages.byPanel.PanelGroup.status.online :
      lang_choiced.pages.byPanel.PanelGroup.status.offline;

  const BadgeColorDetector = (prod) => (prod ? "green" : "red");

  // جابجایی بهینه‌سازها در حالت edit
  const handleMove = (idx, direction) => {
    const newArray = [...optimizersState];
    const newIndex = idx + direction;

    if (newIndex < 0 || newIndex >= newArray.length) return;

    // جابجایی آیتم‌ها
    [newArray[idx], newArray[newIndex]] = [newArray[newIndex], newArray[idx]];

    // به‌روزرسانی order_index
    const [p1, p2] = (PO_group.order_index || "1-1").split("-");
    const updatedArray = newArray.map((opt, i) => ({
      ...opt,
      order_index: `${p1}-${p2}-${i + 1}`,
    }));

    setOptimizersState(updatedArray);

    // به‌روزرسانی لیست کلی
    setPanelOrderList((prev) => {
      const next = [...prev];
      updatedArray.forEach((opt) => {
        const existingIndex = next.findIndex((p) => p.serial_number === opt.serial_number);
        if (existingIndex !== -1) {
          next[existingIndex].order = opt.order_index;
        } else {
          next.push({ serial_number: opt.serial_number, order: opt.order_index });
        }
      });
      return next;
    });
  };

  // تأیید ترتیب جدید
  const handleConfirmOrder = async () => {
    const payload = optimizersState.map((o) => ({
      serial_number: o.serial_number,
      order_index: o.order_index,
    }));

    console.log("Final order list to save:", payload);

    try {
      // await axios.post('/api/update-order', payload);
      // alert(lang_choiced.pages.byPanel.PanelGroup.orderSaved);
    } catch (error) {
      console.error("Error saving order:", error);
      // alert(lang_choiced.pages.byPanel.PanelGroup.orderError);
    }
  };

  // تابع wrapper برای setDateHOC
  const handleSetDateHOC = (newDates) => {
    console.log(`PanelsGroup ${index} - New dates:`, newDates);

    // اطمینان از فرمت صحیح
    const formattedDates = [
      toJalaliString(newDates[0]),
      toJalaliString(newDates[1])
    ];

    setDateHOC(formattedDates);
  };

  // محاسبه توان کل گروه
  const calculateGroupPower = () => {
    if (!group_data) return 0;
    return (group_data.Vline * group_data.Iline) / 10000;
  };

  return (
    <Badge.Ribbon
      text={BadgeTextDetector(group_data)}
      color={BadgeColorDetector(group_data)}
      placement="end"
    >
      <Card key={`card-panel-${index}`} className="mb-4 shadow-sm">
        <CardHeader className="bg-light">
          <Row className="align-items-center">
            <Col md={12}>
              <b className="h5">
                {lang_choiced.pages.byPanel.PanelGroup.groupTitle} {index}
              </b>
              {" "}
              <span className="text-muted">
                ({lang_choiced.pages.byPanel.PanelGroup.serialNumberTitle}: {serial_number || "-"})
              </span>
            </Col>
          </Row>
        </CardHeader>

        <div className="string-label-bypanel py-2 bg-white">
          <Row className="content-label-bypanel align-items-center">
            <Col md={3} className="text-center">
              <ReactStrapBadge className="p-2 px-3" color={group_data ? "success" : "secondary"}>
                <b style={{ fontSize: "16px" }}>
                  {lang_choiced.pages.byPanel.PanelGroup.Line.Pout} :{" "}
                  {group_data !== null
                    ? setPowerNumber(Math.round(calculateGroupPower() * 1000) / 1000)
                    : " - "}
                  <span className="mr-1">
                    {group_data && setPowerUnit(calculateGroupPower())}
                  </span>
                </b>
              </ReactStrapBadge>
            </Col>

            <Col md={3} className="text-center">
              <span className="font-weight-bold">
                {lang_choiced.pages.byPanel.PanelGroup.Line.I} :{" "}
              </span>
              {group_data !== null ? (group_data.Iline / 1000).toFixed(2) : " - "}
              <span className="mr-1">A</span>
            </Col>

            <Col md={3} className="text-center">
              <span className="font-weight-bold">
                {lang_choiced.pages.byPanel.PanelGroup.Line.V} :{" "}
              </span>
              {group_data !== null ? Math.floor(group_data.Vline / 10) : " - "}
              <span className="mr-1">V</span>
            </Col>

            <Col md={3} className="text-center">
              <Button
                color="primary"
                onClick={() => setShowModalLine(true)}
                outline
                className="d-flex align-items-center justify-content-center mx-auto"
                style={{ width: "fit-content" }}
              >
                <AreaChartOutlined style={{ verticalAlign: "middle", fontSize: "16px" }} />
                <span className="mr-2" style={{ fontSize: "14px" }}>
                  {lang_choiced.pages.byPanel.PanelGroup.Line.btnModalLine}
                </span>
              </Button>
            </Col>
          </Row>
        </div>
        {/* <div>
          <Row className="align-items-center py-2">
            <Col>I<sub>neutral:</sub></Col>
            <Col>P<sub>neutral</sub></Col>
            <Col></Col>
          </Row>
        </div> */}

        <CardBody style={{ overflowX: "auto", backgroundColor: "#f8f9fa" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${itemsPerRow}, minmax(100px, 1fr))`,
              gap: "12px",
              transition: "all 0.3s ease",
              width: "fit-content",
              margin: "0 auto",
              padding: "10px",
            }}
          >
            {optimizersState.map((optimizer, idx) => (
              <div
                key={`${optimizer.serial_number}-${idx}`}
                style={{
                  position: "relative",
                  transition: "transform 0.3s ease",
                }}
              >
                <Pannel
                  DataGetStatus={optimizer.Last_Production?.length > 0}
                  lang_choiced={lang_choiced}
                  optimizer={optimizer}
                  index={idx}
                  dateHOC={dateHOC}
                  setDateHOC={handleSetDateHOC}
                  editMode={editMode}
                  panelsPerRow={itemsPerRow}
                  group_index={PO_group.order_index || "1-1"}
                />

                {editMode && (
                  <div
                    style={{
                      position: "absolute",
                      top: "6px",
                      right: "6px",
                      display: "flex",
                      gap: "4px",
                      background: "rgba(255, 255, 255, 0.9)",
                      borderRadius: "6px",
                      padding: "3px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      zIndex: 10,
                    }}
                  >
                    <Button
                      size="sm"
                      color="light"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMove(idx, -1);
                      }}
                      disabled={idx === 0}
                      style={{ padding: "2px 6px", minWidth: "30px" }}
                    >
                      <RightOutlined style={{ fontSize: "12px" }} />
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMove(idx, 1);
                      }}
                      disabled={idx === optimizersState.length - 1}
                      style={{ padding: "2px 6px", minWidth: "30px" }}
                    >
                      <LeftOutlined style={{ fontSize: "12px" }} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {editMode && (
            <div className="text-center mt-4 pt-3 border-top">
              <Button
                color="primary"
                onClick={handleConfirmOrder}
                className="px-4"
              >
                {lang_choiced.pages.byPanel.PanelGroup.saveOrder || "ثبت ترتیب جدید"}
              </Button>
            </div>
          )}
        </CardBody>

        {/* مودال نمایش چارت خط */}
        <ModalLine
          lang_choiced={lang_choiced}
          showModal={showModalLine}
          setShowModal={setShowModalLine}
          Gateway_id={PO_group.gateway?.group_data?.[0]?.Gateway_id}
          optimizers={optimizersState}
          dateHOC={dateHOC}
          setDateHOC={handleSetDateHOC}
          Last_Productions={Last_Productions}
          serial_number={serial_number}
          gateway={PO_group.gateway}
          active_balancer={PO_group.active_balancer}
        />
      </Card>
    </Badge.Ribbon>
  );
};

export default React.memo(PanelsGroup);